import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import QuickPayModal from '../../components/QuickPayModal';
import DeliveryDriverDashboard from '../../components/DeliveryDriverDashboard';
import ReferralProgram from '../../components/ReferralProgram';
import ProviderDocuments from '../../components/ProviderDocuments';
import EnhancedEarningsTab from '../../components/EnhancedEarningsTab';
import { ExportPDFButton } from '../../components/ExportPDFButton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import useProviderJobs from '../../hooks/useProviderJobs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MapPin, DollarSign, Star, Clock, Loader2, ChevronRight,
  Truck, Battery, Car, Key, Wrench, Shield, Navigation, Briefcase,
  Timer, Check, X, PlayCircle, CheckCircle, Wallet, Zap, Package,
  FileText, Gift, TrendingUp, Phone, ArrowRight, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = {
  tow: Truck, towing: Truck, jump_start: Battery, flat_tire: Car,
  tire_change: Car, lockout: Key, repair: Wrench, diagnostic: Shield,
  oil_change: Wrench, brake: Shield, battery: Battery
};

// --- DoorDash Dasher‑style Provider Dashboard ---

export default function ProviderDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [isOnline, setIsOnline] = useState(user?.is_online || false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('home');
  const [allJobs, setAllJobs] = useState([]);
  const [jobOffers, setJobOffers] = useState([]);
  const [stats, setStats] = useState({ total_jobs: 0, total_earnings: 0, rating: 5.0 });
  const [location, setLocation] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showQuickPay, setShowQuickPay] = useState(false);

  useEffect(() => {
    fetchData();
    getCurrentLocation();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
    // fetchData / getCurrentLocation are stable in practice; re-running on
    // every render would spam the API. Intentional one-shot effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, statsRes, offersRes] = await Promise.all([
        axios.get(`${API}/services`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/stats/mechanic`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/jobs/offers`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setAllJobs(reqRes.data);
      setStats(statsRes.data);
      setJobOffers(offersRes.data || []);
    } catch (e) {
      console.error('Fetch error:', e);
    } finally { setLoading(false); }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => { const loc = { lat: p.coords.latitude, lng: p.coords.longitude }; setLocation(loc); updateLoc(loc); },
      () => {}, { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  };

  const updateLoc = async (loc) => {
    try {
      await axios.put(`${API}/mechanics/location`, { location: loc }, { headers: getAuthHeaders(), withCredentials: true });
    } catch (err) {
      console.debug('updateLoc failed', err);
    }
  };

  const handleToggle = async (checked) => {
    setUpdatingStatus(true);
    try {
      const res = await axios.put(`${API}/mechanics/status`, { is_online: checked, location }, { headers: getAuthHeaders(), withCredentials: true });
      setIsOnline(res.data?.is_online ?? checked);
      toast.success(checked ? 'You are now online' : 'You are now offline');
    } catch (err) {
      console.debug('toggle online status failed', err);
      setIsOnline(checked);
      toast.success(checked ? 'Online' : 'Offline');
    }
    setUpdatingStatus(false);
  };

  const acceptOffer = async (offerId) => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/accept`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Job accepted!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to accept'); }
  };

  const declineOffer = async (offerId) => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/decline`, { reason: 'Not available' }, { headers: getAuthHeaders(), withCredentials: true });
      toast.info('Job declined');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to decline'); }
  };

  const uid = user?.user_id;
  const activeJobs = allJobs.filter(j => j.mechanic_id === uid && ['accepted', 'assigned', 'en_route', 'arrived', 'in_progress'].includes(j.status));
  const completedJobs = allJobs.filter(j => j.mechanic_id === uid && j.status === 'completed');
  const pendingOffers = jobOffers.filter(o => o.status === 'pending' || !o.status);

  const todayEarnings = stats.today_earned || (stats.total_earnings * 0.15) || 0;
  const weekEarnings = stats.week_earned || (stats.total_earnings * 0.4) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-white" data-testid="provider-dashboard">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#FF3008' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="provider-dashboard">
      <Header />

      {/* DoorDash-style bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E8E8] z-50 flex" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {[
          { key: 'home', icon: '●', label: 'Home' },
          { key: 'earnings', icon: '$', label: 'Earnings' },
          { key: 'ratings', icon: '★', label: 'Ratings' },
          { key: 'account', icon: '■', label: 'Account' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            className={`flex-1 flex flex-col items-center py-3 text-xs font-medium transition-colors ${view === tab.key ? 'text-[#FF3008]' : 'text-[#AFAFAF]'}`}
            data-testid={`tab-${tab.key}`}
          >
            <span className="text-lg mb-0.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <main className="max-w-3xl mx-auto px-4 pb-24 pt-4">
        {/* ───── HOME VIEW ───── */}
        {view === 'home' && (
          <div className="space-y-4">
            {/* Top Status Bar */}
            <div className="bg-white rounded-2xl p-4 flex items-center justify-between shadow-sm border border-[#E8E8E8]" data-testid="status-bar">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-[#00B37E] animate-pulse' : 'bg-[#AFAFAF]'}`} />
                <span className="font-semibold text-[#191919] text-sm" data-testid="online-status-text">
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="text-center">
                <p className="text-[11px] text-[#767676]">Today</p>
                <p className="text-lg font-extrabold text-[#191919]" data-testid="today-earnings">${todayEarnings.toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold text-[#191919]">{(stats.rating || 5.0).toFixed(1)}</span>
                <Star className="w-3.5 h-3.5 text-[#FFAA00] fill-[#FFAA00]" />
              </div>
            </div>

            {/* Active Job Banner */}
            {activeJobs.length > 0 && (
              <button
                onClick={() => navigate(`/provider/job/${activeJobs[0].request_id}`)}
                className="w-full bg-[#00B37E] rounded-2xl p-4 flex items-center gap-3 text-white shadow-lg"
                data-testid="active-job-banner"
              >
                <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                <div className="flex-1 text-left">
                  <p className="font-bold text-base capitalize">
                    {activeJobs[0].service_type?.replace(/_/g, ' ') || 'Service'}
                  </p>
                  <p className="text-sm text-white/80">
                    {activeJobs[0].status === 'accepted' || activeJobs[0].status === 'assigned' ? 'Head to location' :
                     activeJobs[0].status === 'en_route' ? 'On the way' :
                     activeJobs[0].status === 'arrived' ? 'You have arrived' : 'In progress'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5" />
              </button>
            )}

            {/* Incoming Offers */}
            {pendingOffers.length > 0 && pendingOffers.map(offer => {
              const expires = new Date(offer.expires_at);
              const timeLeft = Math.max(0, Math.floor((expires - new Date()) / 1000));
              return (
                <div key={offer.offer_id} className="bg-white rounded-2xl p-5 border border-[#E8E8E8] shadow-sm" data-testid={`offer-${offer.offer_id}`}>
                  <div className="flex items-center gap-4 mb-4">
                    {/* Timer */}
                    <div className="w-14 h-14 rounded-full border-[3px] border-[#FF3008] flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-extrabold text-[#FF3008]">{timeLeft > 60 ? Math.ceil(timeLeft / 60) : timeLeft}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-[11px] text-[#767676]">Guaranteed</p>
                      <p className="text-3xl font-extrabold text-[#191919]" data-testid="offer-pay">
                        ${offer.total_pay || (offer.provider_pay || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-[#F5F5F5] px-3 py-1.5 rounded-full">
                      <span className="text-sm font-semibold text-[#191919]">{offer.estimated_miles || '?'} mi</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FF3008]" />
                    <span className="font-semibold text-[#191919] capitalize">{offer.service_name || offer.service_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm text-[#767676] ml-5 mb-1">Customer: {offer.customer_name}</p>
                  {offer.location?.address && <p className="text-sm text-[#767676] ml-5 mb-4 truncate">{offer.location.address}</p>}

                  <div className="flex gap-2">
                    <button
                      onClick={() => declineOffer(offer.offer_id)}
                      className="flex-1 py-3.5 rounded-full bg-[#F5F5F5] text-[#767676] font-semibold text-base"
                      data-testid="decline-offer-btn"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => acceptOffer(offer.offer_id)}
                      className="flex-[2] py-3.5 rounded-full bg-[#00B37E] text-white font-bold text-lg"
                      data-testid="accept-offer-btn"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Dash Now / End Dash */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E8E8E8]">
              {isOnline ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-[#191919]">Looking for offers...</p>
                    <p className="text-sm text-[#767676]">
                      {pendingOffers.length} pending{activeJobs.length > 0 ? ` · ${activeJobs.length} active` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle(false)}
                    disabled={updatingStatus}
                    className="w-14 h-14 rounded-full border-2 border-[#FF3B30] flex items-center justify-center"
                    data-testid="end-dash-btn"
                  >
                    {updatingStatus ? <Loader2 className="w-5 h-5 animate-spin text-[#FF3B30]" /> :
                      <span className="text-[#FF3B30] font-bold text-sm">End</span>}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleToggle(true)}
                  disabled={updatingStatus}
                  className="w-full py-5 rounded-full text-white font-extrabold text-2xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: '#FF3008' }}
                  data-testid="dash-now-btn"
                >
                  {updatingStatus ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : (
                    <>
                      Dash Now
                      <p className="text-sm font-normal text-white/80 mt-0.5">Start receiving job offers</p>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#191919]">{stats.total_jobs || 0}</p>
                <p className="text-xs text-[#767676]">Jobs</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#191919]">
                  ${stats.total_jobs > 0 ? (stats.total_earnings / stats.total_jobs).toFixed(0) : '0'}
                </p>
                <p className="text-xs text-[#767676]">Avg/Job</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#00B37E]">${(stats.pending_payout || stats.total_earnings || 0).toFixed(2)}</p>
                <p className="text-xs text-[#767676]">Balance</p>
              </div>
            </div>

            {/* Active Jobs List */}
            {activeJobs.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-[#191919] mb-3">Active Jobs</h2>
                {activeJobs.map(job => <JobRow key={job.request_id} job={job} onClick={() => navigate(`/provider/job/${job.request_id}`)} />)}
              </div>
            )}

            {/* Recent Completed */}
            {completedJobs.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-[#191919] mb-3">Recent Activity</h2>
                {completedJobs.slice(0, 5).map(job => <JobRow key={job.request_id} job={job} onClick={() => navigate(`/provider/job/${job.request_id}`)} completed />)}
              </div>
            )}
          </div>
        )}

        {/* ───── EARNINGS VIEW ───── */}
        {view === 'earnings' && (
          <div className="space-y-4">
            {/* Dark Hero */}
            <div className="bg-[#191919] rounded-2xl px-5 py-8 text-center">
              <p className="text-sm text-white/50">Total Earnings</p>
              <p className="text-5xl font-extrabold text-white mt-2 mb-4">${(stats.total_earnings || 0).toFixed(2)}</p>
              <div className="flex justify-center gap-2">
                {['Today', 'Week', 'All'].map(p => (
                  <span key={p} className="px-5 py-2 rounded-full text-sm font-semibold cursor-pointer bg-white/10 text-white/60 hover:bg-white hover:text-[#191919] transition-colors">
                    {p}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#191919]">{stats.total_jobs || 0}</p>
                <p className="text-xs text-[#767676] mt-1">Jobs</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#191919]">
                  ${stats.total_jobs > 0 ? (stats.total_earnings / stats.total_jobs).toFixed(0) : '0'}
                </p>
                <p className="text-xs text-[#767676] mt-1">Avg/Job</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-[#E8E8E8]">
                <p className="text-xl font-bold text-[#00B37E]">${(stats.pending_payout || 0).toFixed(2)}</p>
                <p className="text-xs text-[#767676] mt-1">Pending</p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-white rounded-2xl p-5 border border-[#E8E8E8]">
              <h3 className="font-bold text-[#191919] mb-4">Earnings Breakdown</h3>
              {[
                ['Base Pay', (stats.total_earnings || 0).toFixed(2)],
                ['Tips', (stats.tips_total || 0).toFixed(2)],
                ['Bonuses', (stats.bonus_total || 0).toFixed(2)],
              ].map(([label, val]) => (
                <div key={label}>
                  <div className="flex justify-between py-3">
                    <span className="text-[#767676]">{label}</span>
                    <span className="font-semibold text-[#191919]">${val}</span>
                  </div>
                  <div className="h-px bg-[#F0F0F0]" />
                </div>
              ))}
              <div className="flex justify-between py-3">
                <span className="font-bold text-[#191919]">Total</span>
                <span className="font-extrabold text-xl text-[#191919]">${(stats.total_earnings || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* Cash Out */}
            {(stats.total_earnings || 0) > 0 && (
              <button
                onClick={() => { setShowQuickPay(true); }}
                className="w-full py-4 rounded-full text-white font-bold text-lg text-center"
                style={{ background: '#FF3008' }}
                data-testid="cash-out-btn"
              >
                Cash Out ${(stats.pending_payout || stats.total_earnings || 0).toFixed(2)}
                <p className="text-xs text-white/70 font-normal mt-0.5">Instant transfer to your bank</p>
              </button>
            )}

            {/* Activity */}
            <h3 className="font-bold text-[#191919] mt-2">Recent Activity</h3>
            {completedJobs.length > 0 ? completedJobs.slice(0, 8).map(job => (
              <div key={job.request_id} className="flex items-center justify-between py-3 border-b border-[#F0F0F0]">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00B37E]" />
                  <div>
                    <p className="font-medium text-[#191919] capitalize">{job.service_type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-[#AFAFAF]">{new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  </div>
                </div>
                <span className="font-bold text-[#00B37E]">+${(job.provider_pay || job.total_price * 0.3 || 0).toFixed(2)}</span>
              </div>
            )) : <p className="text-[#AFAFAF] text-center py-8">No earnings yet</p>}
          </div>
        )}

        {/* ───── RATINGS VIEW ───── */}
        {view === 'ratings' && (
          <div className="space-y-4">
            <div className="bg-[#191919] rounded-2xl px-5 py-8 text-center">
              <p className="text-xs text-white/40 uppercase tracking-widest">Your Ratings</p>
              <div className="flex items-baseline justify-center mt-2 mb-1">
                <span className="text-5xl font-extrabold text-white">{(stats.rating || 5.0).toFixed(1)}</span>
                <Star className="w-8 h-8 text-[#FFAA00] fill-[#FFAA00] ml-1" />
              </div>
              <p className="text-sm text-white/40">{stats.total_jobs || 0} completed jobs</p>
            </div>

            {/* Stat Rings */}
            <div className="bg-white rounded-2xl p-5 border border-[#E8E8E8] flex justify-around">
              {[
                { val: (stats.rating || 5.0).toFixed(1), label: 'Rating', color: '#00B37E', suffix: '★' },
                { val: Math.min(95, 70 + (stats.total_jobs || 0) * 2), label: 'Acceptance', color: '#FF3008', suffix: '%' },
                { val: Math.min(99, 85 + (stats.total_jobs || 0)), label: 'Completion', color: '#FFAA00', suffix: '%' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className="w-[72px] h-[72px] rounded-full border-4 flex items-center justify-center mx-auto" style={{ borderColor: s.color }}>
                    <div>
                      <span className="text-xl font-extrabold" style={{ color: s.color }}>{typeof s.val === 'number' ? Math.round(s.val) : s.val}</span>
                      <span className="text-[10px] text-[#AFAFAF] block -mt-1">{s.suffix}</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#767676] mt-2">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Performance Details */}
            <div className="bg-white rounded-2xl p-5 border border-[#E8E8E8]">
              <h3 className="font-bold text-[#191919] mb-4">Performance</h3>
              {[
                { label: 'Customer Rating', val: `${(stats.rating || 5.0).toFixed(2)} / 5.0`, color: '#00B37E' },
                { label: 'Acceptance Rate', val: `${Math.min(95, 70 + (stats.total_jobs || 0) * 2)}%`, color: '#FF3008' },
                { label: 'Completion Rate', val: `${Math.min(99, 85 + (stats.total_jobs || 0))}%`, color: '#FFAA00' },
                { label: 'On-Time Rate', val: `${Math.min(98, 80 + (stats.total_jobs || 0) * 1.5)}%`, color: '#8B5CF6' },
                { label: 'Total Jobs', val: stats.total_jobs || 0, color: '#191919' },
              ].map(s => (
                <div key={s.label}>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                      <span className="text-[#767676]">{s.label}</span>
                    </div>
                    <span className="font-semibold text-[#191919]">{s.val}</span>
                  </div>
                  <div className="h-px bg-[#F0F0F0]" />
                </div>
              ))}
            </div>

            {/* Tips */}
            <div className="bg-white rounded-2xl p-5 border border-[#E8E8E8]">
              <h3 className="font-bold text-[#191919] mb-3">How to improve your ratings</h3>
              {['Be punctual — arrive within the estimated time', 'Communicate clearly with customers', 'Maintain a high acceptance rate for better offers'].map((t, i) => (
                <div key={t} className="flex items-start gap-3 mb-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5F5F5] text-[11px] font-bold text-[#767676] flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-[#767676] leading-5">{t}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ───── ACCOUNT VIEW ───── */}
        {view === 'account' && (
          <div className="space-y-4">
            {/* Profile Header */}
            <div className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-[#E8E8E8]">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ background: '#FF3008' }}>
                {user?.name?.[0] || 'J'}
              </div>
              <div className="flex-1">
                <p className="font-bold text-[#191919] text-lg">{user?.name}</p>
                <p className="text-sm text-[#767676] capitalize">{
                  user?.user_type === 'mechanic' ? 'Mobile Mechanic' :
                  user?.user_type === 'roadside_tech' ? 'Roadside Tech' :
                  user?.user_type?.replace(/_/g, ' ') || 'Provider'
                }</p>
              </div>
              <div className="flex items-center gap-1 bg-[#F5F5F5] px-3 py-1.5 rounded-full">
                <span className="text-sm font-bold text-[#191919]">{(stats.rating || 5.0).toFixed(1)}</span>
                <Star className="w-3.5 h-3.5 text-[#FFAA00] fill-[#FFAA00]" />
              </div>
            </div>

            {/* Account Section */}
            <p className="text-[11px] font-semibold text-[#AFAFAF] uppercase tracking-widest ml-1">Account</p>
            <div className="bg-white rounded-2xl border border-[#E8E8E8] divide-y divide-[#F0F0F0]">
              <AccountRow label="Email" value={user?.email} />
              <AccountRow label="Phone" value={user?.phone || 'Not set'} />
              <AccountRow label="Member Since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : '-'} />
            </div>

            {/* Dasher Section */}
            <p className="text-[11px] font-semibold text-[#AFAFAF] uppercase tracking-widest ml-1">Dasher</p>
            <div className="bg-white rounded-2xl border border-[#E8E8E8] divide-y divide-[#F0F0F0]">
              <AccountLink label="Vehicle Information" onClick={() => navigate('/settings')} />
              <AccountLink label="Identity Verification" onClick={() => navigate('/settings')} />
              <AccountLink label="Payment Methods" onClick={() => navigate('/employee/settings')} />
              <AccountLink label="Documents" onClick={() => setView('documents')} />
              <AccountLink label="Deliveries" onClick={() => setView('deliveries')} />
              <AccountLink label="Referral Program" onClick={() => setView('referrals')} />
            </div>

            {/* General */}
            <p className="text-[11px] font-semibold text-[#AFAFAF] uppercase tracking-widest ml-1">General</p>
            <div className="bg-white rounded-2xl border border-[#E8E8E8] divide-y divide-[#F0F0F0]">
              <AccountLink label="Notification Settings" onClick={() => navigate('/settings')} />
              <AccountLink label="Help & Support" onClick={() => {}} />
              <AccountLink label="Terms of Service" onClick={() => {}} />
              <AccountLink label="Privacy Policy" onClick={() => {}} />
            </div>

            <ExportPDFButton reportType="provider" label="Export My Report" />

            <p className="text-center text-xs text-[#AFAFAF] mt-4">Jiffys Mobile v1.0.0</p>
          </div>
        )}

        {/* ───── SUB-VIEWS ───── */}
        {view === 'documents' && (
          <div>
            <button onClick={() => setView('account')} className="text-sm text-[#FF3008] font-semibold mb-4">← Back to Account</button>
            <ProviderDocuments />
          </div>
        )}
        {view === 'deliveries' && (
          <div>
            <button onClick={() => setView('account')} className="text-sm text-[#FF3008] font-semibold mb-4">← Back to Account</button>
            <DeliveryDriverDashboard />
          </div>
        )}
        {view === 'referrals' && (
          <div>
            <button onClick={() => setView('account')} className="text-sm text-[#FF3008] font-semibold mb-4">← Back to Account</button>
            <ReferralProgram />
          </div>
        )}
      </main>

      <QuickPayModal isOpen={showQuickPay} onClose={() => setShowQuickPay(false)} availableBalance={stats.total_earnings || 0} />
    </div>
  );
}

/* ── Helper Components ── */

function JobRow({ job, onClick, completed }) {
  const Icon = serviceIcons[job.service_type] || Car;
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl border border-[#E8E8E8] mb-2 text-left hover:border-[#FF3008]/30 transition-colors">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: completed ? '#E6F9F3' : '#FFF0ED' }}>
        <Icon className="w-5 h-5" style={{ color: completed ? '#00B37E' : '#FF3008' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#191919] text-sm capitalize truncate">{job.service_type?.replace(/_/g, ' ')}</p>
        <p className="text-xs text-[#AFAFAF] truncate">{job.customer_name || 'Customer'} · {job.vehicle_make} {job.vehicle_model}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {completed ? (
          <span className="text-sm font-bold text-[#00B37E]">+${(job.provider_pay || job.total_price * 0.3 || 0).toFixed(2)}</span>
        ) : (
          <span className="text-xs font-semibold text-[#FF3008] capitalize">{job.status?.replace(/_/g, ' ')}</span>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-[#AFAFAF] flex-shrink-0" />
    </button>
  );
}

function AccountRow({ label, value }) {
  return (
    <div className="flex justify-between items-center px-4 py-3.5">
      <span className="text-sm text-[#767676]">{label}</span>
      <span className="text-sm font-medium text-[#191919] truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}

function AccountLink({ label, onClick }) {
  return (
    <button onClick={onClick} className="w-full flex justify-between items-center px-4 py-3.5 hover:bg-[#F5F5F5] transition-colors text-left">
      <span className="text-[#191919]">{label}</span>
      <span className="text-xl text-[#AFAFAF]">›</span>
    </button>
  );
}
